<script setup lang="ts">
import { IArrival, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import EzTable from './ezTable/EzTable.vue';
import EzHeader from './ezTable/EzHeader.vue';
import EzCell from './ezTable/EzCell.vue';
import { i18n } from '@/i18n';
import { computed, ref, watch } from 'vue';
import EzTableRow from './ezTable/EzTableRow.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import EditableTableCell from '../BaseTableContent/components/EditableTableCell.vue';
import { convertIDateToDateFormat } from '../BaseTableContent/utils/dateFormat';
import OrderStatusSelectInput from './OrderStatusSelectInput.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { simpleCopy } from '@/utils/deepCopy';
import InfoModal from './InfoModal.vue';
import { IDate } from '@/coreDomainsModule/date';
import { showInfoModal } from './InfoModal/InfoModalManager';

const HEADER_KEY_LIST = ['', '発注日', '発注番号', '納期', '発注数量', 'ステータス'] as const;

const gridTemplateColumns = ['48px', '130px', '180px', '180px', '130px', '130px'];
const exceptedOrderStatus: OrderStatus[] = [
  OrderStatus.Recommended,
  OrderStatus.PartialDelivery,
  OrderStatus.Unknown,
  OrderStatus.DisplayEnded,
  OrderStatus.NoRecommendation,
];

const t = i18n.t;

const props = withDefaults(
  defineProps<{
    modalState: boolean;
    unpaidOrderedOrders: IArrival[];
    executionDate?: Date;
  }>(),
  {
    executionDate: undefined,
  }
);

const emits = defineEmits<{ (e: 'confirm', value: IArrival[]); (e: 'close') }>();

const expandedOrderId = ref('');
const editingOrders = ref<IArrival[]>([]);

const displayOrderData = computed(() => {
  if (!props.unpaidOrderedOrders || editingOrders.value.length === 0) return [];
  // 分納展開時
  if (expandedOrderId.value) {
    return editingOrders.value.flatMap((order) =>
      expandedOrderId.value === order.arrivalId ? [order, ...(order.partialDeliveries ?? [])] : [order]
    );
  }
  return editingOrders.value;
});

/**
 * テーブルセルの下線をつけるかどうか
 * @param orderId - 発注/分納ID
 * @param index - インデックス
 */
const tableCellHasBottomBorder = (orderId: string, index: number) => {
  const lastPartialDeliveryIndex =
    displayOrderData.value.findIndex((order) => order.arrivalId === expandedOrderId.value) +
    (displayOrderData.value.find((order) => order.arrivalId === expandedOrderId.value)?.partialDeliveries?.length ?? 0);
  // NOTE: PDから始まるIDは分納の子情報
  return !(orderId === expandedOrderId.value || orderId.startsWith('PD')) || index === lastPartialDeliveryIndex;
};

/**
 * 分納を展開しているIDをセットする
 * @param orderId - 発注ID
 */
const setExpandedOrderId = (orderId: string) => {
  expandedOrderId.value = orderId;
};

/**
 * 分納を折りたたむ
 */
const unsetExpandedOrderId = () => {
  expandedOrderId.value = '';
};

/**
 * 納期のアップデート
 * @param orderId - 発注/分納ID
 * @param date - 変更後の納期
 */
const updateOrderDate = (orderId: string, date: IDate) => {
  if (orderId.startsWith('PD')) {
    // 分納の場合
    // 親の発注を取得
    const targetParentOrder = editingOrders.value.find((order) => order.arrivalId === expandedOrderId.value);
    if (!targetParentOrder || !targetParentOrder.partialDeliveries) return;
    // 親の発注から子の発注を取得
    const targetOrder = targetParentOrder.partialDeliveries.find((partialDelivery) => partialDelivery.arrivalId === orderId);
    if (!targetOrder) return;
    if (!targetParentOrder.crossDate) {
      // 親の納期がない場合はこの分納の納期を設定
      targetParentOrder.crossDate = date;
    } else if (DateService.compareDates(targetParentOrder.crossDate, date) === -1) {
      // 変更する値が親の現在の納期より後の場合は親の納期をそれに合わせる
      targetParentOrder.crossDate = date;
    } else if (
      targetOrder.crossDate &&
      DateService.compareDates(targetParentOrder.crossDate, targetOrder.crossDate) === 0 &&
      DateService.compareDates(targetParentOrder.crossDate, date) === 1
    ) {
      // 親の納期が変更前の分納の納期と同じで、変更後の分納の納期が親の納期より前になる場合
      // この場合、持っている分納の中から最も遅い納期を親の納期にする
      targetParentOrder.crossDate = targetParentOrder.partialDeliveries
        .map((partialDelivery) => (partialDelivery.arrivalId === orderId ? date : partialDelivery.crossDate))
        .filter((pdCrossDate) => pdCrossDate !== undefined)
        .sort((a, b) => DateService.compareDates(a, b))
        .at(-1);
    }
    targetOrder.crossDate = date;
  } else {
    // 普通の発注の場合
    const targetOrder = editingOrders.value.find((order) => order.arrivalId === orderId);
    if (!targetOrder) return;
    targetOrder.crossDate = date;
  }
};

/**
 * 発注ステータスのアップデート
 * @param orderId - 発注/分納ID
 * @param value - 変更後の発注ステータス
 */
const updateOrderStatus = (orderId: string, value: OrderStatus) => {
  // 選択肢にない発注ステータスに変えようとしていたら何もせず終了
  if (exceptedOrderStatus.includes(value)) return;
  // NOTE: PDから始まるIDは分納の子情報
  if (orderId.startsWith('PD')) {
    // 分納の場合
    // 親の発注を取得
    const targetParentOrder = editingOrders.value.find((order) => order.arrivalId === expandedOrderId.value);
    if (!targetParentOrder || !targetParentOrder.partialDeliveries) return;
    // 親の発注から子の発注を取得
    const targetOrder = targetParentOrder.partialDeliveries.find((partialDelivery) => partialDelivery.arrivalId === orderId);
    if (!targetOrder) return;
    targetOrder.status = value;
  } else {
    // 普通の発注の場合
    // 対象の発注を取得
    const targetOrder = editingOrders.value.find((order) => order.arrivalId === orderId);
    // ステータスが今と同じか、分納なら何もしない
    if (!targetOrder || targetOrder.status === value || targetOrder.status === OrderStatus.PartialDelivery) return;
    targetOrder.status = value;
  }
};

/**
 * 確定ボタン押下時の処理
 */
const onNext = async () => {
  /**
   * 発注のバリデーション
   * @param order - 発注<IArrival>
   */
  const _validationOrder = (order: IArrival) => {
    if (!(order.crossDate && props.executionDate)) return false;
    if (order.status === OrderStatus.Delivered) {
      return true;
    } else {
      if (DateService.compareDates(order.crossDate, DateService.convertDateToIDate(props.executionDate)) !== 1) {
        return false;
      }
    }
    if (order.status === OrderStatus.PartialDelivery && order.partialDeliveries) {
      return order.partialDeliveries.every(_validationOrder);
    }
    return true;
  };

  const editedOrder = simpleCopy(editingOrders.value);
  if (!editedOrder.every(_validationOrder)) {
    // 確認モーダル
    const isProceedDespiteInvalidOrders = await showInfoModal({
      modalTitle: t('発注済み未納が残っています'),
      modalDescription: t(
        'ステータスが納入済みでなく、ExecutionDateより前に納期が設定されている発注が残っています。このまま進めてよろしいですか?'
      ),
      backButtonLabel: t('キャンセル'),
      nextButtonLabel: t('続行'),
      enableOutsideClick: false,
    });
    if (!isProceedDespiteInvalidOrders) return;
  }
  emits('confirm', editedOrder);
};

watch(
  () => props.unpaidOrderedOrders,
  (newValue) => {
    editingOrders.value = simpleCopy(newValue);
  },
  { immediate: true }
);

watch(
  () => props.modalState,
  (newValue) => {
    if (!newValue) {
      expandedOrderId.value = '';
    }
  }
);
</script>
<template>
  <InfoModal
    :modal-state="modalState"
    :next-button-label="t('確定')"
    :back-button-label="t('キャンセル')"
    @on-next="onNext"
    @on-back="emits('close')"
  >
    <template #title> {{ t('発注済み未納編集') }}</template>
    <template #description>{{ t('発注済み未納の納期、またはステータスを変更してください。') }}</template>
    <template #default>
      <EzTable :grid-template-columns="gridTemplateColumns" sticky-header>
        <template #header>
          <template v-for="(key, headerIndex) in HEADER_KEY_LIST" :key="headerIndex">
            <EzHeader class="table-header">{{ t(key) }}</EzHeader>
          </template>
        </template>
        <template #body>
          <EzTableRow
            v-for="(order, orderIndex) in displayOrderData"
            :key="orderIndex"
            :grid-template-columns="gridTemplateColumns"
            :is-expanded="expandedOrderId === order.arrivalId"
          >
            <EzCell class="table-cell table-cell--center" :has-bottom-border="tableCellHasBottomBorder(order.arrivalId, orderIndex)">
              <button v-if="order.partialDeliveries?.length ?? -1 > 0">
                <SvgLoader v-if="expandedOrderId === ''" name="arrowRight" @click="setExpandedOrderId(order.arrivalId)" />
                <SvgLoader v-else name="arrowDown" @click="unsetExpandedOrderId()" />
              </button>
            </EzCell>
            <EzCell class="table-cell" force-center>
              {{ DateService.convertIYearMonthDayToYYYYMMDDWithSlash({ year: order.year, month: order.month, day: order.day }) }}
            </EzCell>
            <EzCell class="table-cell">
              {{ order.number || '--' }}
            </EzCell>
            <EzCell class="table-cell">
              <EditableTableCell
                input-type="IDate"
                :text-value="order.crossDate ? convertIDateToDateFormat(order.crossDate) : undefined"
                @change-date-value="(date) => updateOrderDate(order.arrivalId, date)"
              />
            </EzCell>
            <EzCell is-number class="table-cell">
              {{ order.amount?.addComma() }}
            </EzCell>
            <EzCell class="table-cell table-cell--center" force-center>
              <OrderStatusSelectInput
                :index="100 + orderIndex"
                :model-value="order.status"
                :except-status="
                  order.arrivalId.startsWith('PD') ? [OrderStatus.PartialDelivery, ...exceptedOrderStatus] : exceptedOrderStatus
                "
                :disabled="order.status === OrderStatus.PartialDelivery"
                @update:model-value="(value: OrderStatus) => updateOrderStatus(order.arrivalId, value)"
              />
            </EzCell>
          </EzTableRow>
        </template>
      </EzTable>
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.table-header {
  justify-content: center;
  height: 32px;
}
.table-cell {
  height: 32px;
  &--center {
    justify-content: center;
  }
}
</style>
